import { ForwardedRef, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Form, DateBox, TextArea } from 'devextreme-react';
import { SimpleItem } from 'devextreme-react/form';
import {
  DEFAULT_DATE_VALUE,
  DX_DATE_DISPLAY_FORMAT,
  FORM_STYLING_MODE,
} from 'app-constants';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import { IPartner } from 'types/datatypes/IPartner';

const getPartnerInfo = gql(`query getPartnerInfo($ref: String) {
  infoapi(doc: "contract", ref: $ref) 
}`);

interface PartnerInfoPanelProps {
  partner: IPartner;
 [key:string]: any;
}

export interface IInstance {
    show:()=>void;
}


export const PartnerInfoPanel = forwardRef<IInstance, PartnerInfoPanelProps>((props, ref:ForwardedRef<IInstance>) => {
  const { partner, ...restProps } = props;
  const [ data, setData ] = useState<Array<Record<string, any>>>([]);
  const [ currentPage, setCurrentPage ] = useState(1);
  const popupRef = useRef<Popup>(null);

  useImperativeHandle(ref, () => ({
    show: () => popupRef.current?.instance.show(),
  }));

  useQuery(getPartnerInfo, {
    variables: { ref: partner?.ref },
    onCompleted: (data) => setData(data.infoapi),
    skip: !partner?.isCorporate
  });

  const pageNum = data.length || 0;
  const hasContracts = pageNum > 0;
  const moreThanOne = pageNum > 1;
  const pageInfo = hasContracts ? `${currentPage}/${pageNum}` : '';
  const title = `Договір ${pageInfo}`;
  const pageData = data?.[currentPage - 1];

  return (
    <Popup
      {...restProps}
      ref={popupRef}
      maxWidth={400}
      height='auto'
      title={title}
      closeOnOutsideClick
      dragEnabled={false}
      resizeEnabled={false}
    >
      <ToolbarItem
        widget='dxButton'
        toolbar='top'
        location='before'
        visible={moreThanOne}
        options={{
          icon: 'chevronleft',
          focusStateEnabled: false,
          onClick: () => setCurrentPage((prev) => Math.max(prev - 1, 1)),
        }}
      />
      <ToolbarItem
        widget='dxButton'
        toolbar='top'
        location='before'
        visible={moreThanOne}
        options={{
          icon: 'chevronnext',
          focusStateEnabled: false,
          onClick: () => setCurrentPage((prev) => Math.min(prev + 1, pageNum)),
        }}
      />
      <div className='otk-content-block'>
        <Form
          formData={pageData}
          colCount={2}
          visible={hasContracts}
          height='fit-content'
        >
          <SimpleItem
            itemType='dxTextBox'
            dataField='name'
            label={{ text: 'Назва' }}
            editorOptions={{ readOnly: true, stylingMode: FORM_STYLING_MODE }}
          />

          <SimpleItem
            itemType='dxTextBox'
            dataField='number'
            label={{ text: 'Номер' }}
            editorOptions={{ readOnly: true, stylingMode: FORM_STYLING_MODE }}
          />

          <SimpleItem label={{ text: 'Дата' }}>
            <DateBox
              readOnly
              value={pageData?.date === DEFAULT_DATE_VALUE ?
                null :
                pageData?.date
              }
              displayFormat={DX_DATE_DISPLAY_FORMAT}
              stylingMode={FORM_STYLING_MODE}
            />
          </SimpleItem>

          <SimpleItem label={{ text: 'Термін дії' }}>
            <DateBox
              readOnly
              value={pageData?.valid_before === DEFAULT_DATE_VALUE ?
                null :
                pageData?.valid_before
              }
              displayFormat={DX_DATE_DISPLAY_FORMAT}
              stylingMode={FORM_STYLING_MODE}
            />
          </SimpleItem>

          <SimpleItem
            colSpan={2}
            itemType='dxCheckBox'
            dataField='cont'
            label={{ text: 'Пролонгація', location: 'left' }}
            editorOptions={{ readOnly: true, stylingMode: FORM_STYLING_MODE }}
          />

          <SimpleItem
            colSpan={2}
            itemType='dxTextBox'
            dataField='delay'
            label={{ text: 'Відстрочка' }}
            editorOptions={{ readOnly: true, stylingMode: FORM_STYLING_MODE }}
          />

          <SimpleItem colSpan={2} label={{ text: 'Опис' }}>
            <TextArea
              readOnly
              value={pageData?.description}
              stylingMode={FORM_STYLING_MODE}
              height={90}
            />
          </SimpleItem>
        </Form>
        {!hasContracts && <span>Немає договорів</span>}
      </div>
    </Popup>
  );
});

PartnerInfoPanel.displayName = 'PartnerInfoPanel';

export namespace PartnerInfoPanel {
  export type Props = PartnerInfoPanelProps;
  export type Instance = IInstance;
}
